
import { useState, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Categories from './components/Categories';
import FeaturedArtworks from './components/FeaturedArtworks';
import Newsletter from './components/Newsletter';
import Footer from './components/Footer';

export default function Home() {
  const [cartItems, setCartItems] = useState<number[]>([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [artworks, setArtworks] = useState<any[]>([]);
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    paymentMethod: 'bank-transfer'
  });

  // SEO Schema.org JSON-LD
  useEffect(() => {
    fetchArtworks();
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Nox Sanat",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/vite.svg`,
      "description": "Özgün sanat eserleri koleksiyonu. Yağlı boya ve sulu boya tablolardan oluşan benzersiz sanat eserleri. Online sanat galerisi.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Sanat Sokağı No: 42",
        "addressLocality": "Beyoğlu",
        "addressRegion": "İstanbul",
        "postalCode": "34433",
        "addressCountry": "TR"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+90-212-555-0123",
        "contactType": "customer service",
        "email": "info@noxsanat.com"
      },
      "sameAs": [
        `${import.meta.env.VITE_SITE_URL || "https://example.com"}/gallery`,
        `${import.meta.env.VITE_SITE_URL || "https://example.com"}/favorites`
      ]
    });
    document.head.appendChild(script);

    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Nox Sanat",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "description": "Özgün sanat eserleri koleksiyonu. Yağlı boya ve sulu boya tablolardan oluşan benzersiz sanat eserleri. Online sanat galerisi.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/gallery?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    });
    document.head.appendChild(websiteScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(websiteScript);
    };
  }, []);

  const fetchArtworks = async () => {
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(
        import.meta.env.VITE_PUBLIC_SUPABASE_URL,
        import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
      );

      const { data, error } = await supabase
        .from('artworks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Mock data as fallback
      const mockArtworks = [
        {
          id: 1,
          title: "Gündoğumu Yansımaları",
          artist: "Mehmet Aksoy",
          technique: "Yağlı Boya",
          category: "oil",
          size: "70x50 cm",
          price: "8500",
          year: "2023",
          materials: "Tuval üzerine yağlı boya",
          description: "Bu eser, doğanın en büyüleyici anlarından biri olan gündoğumunu yansıtır.",
          image_url: "https://public.readdy.ai/ai/img_res/24d52210121a6827d5678061226a1089.jpg",
          created_at: "2024-01-01"
        },
        {
          id: 2,
          title: "Sakin Orman",
          artist: "Ayşe Demir",
          technique: "Sulu Boya",
          category: "watercolor",
          size: "60x40 cm",
          price: "4200",
          year: "2024",
          materials: "Sulu boya kağıdı üzerine sulu boya",
          description: "Ormanın derinliklerinde yakalanan bu sessizlik, sulu boyanın şeffaflığı ile mükemmel bir uyum içinde sunulmuştur.",
          image_url: "https://readdy.ai/api/search-image?query=Peaceful%20forest%20landscape%20watercolor%20painting&width=400&height=500&seq=home2&orientation=portrait",
          created_at: "2024-01-02"
        },
        {
          id: 3,
          title: "Şehir Işıkları",
          artist: "Can Yılmaz",
          technique: "Yağlı Boya",
          category: "oil",
          size: "80x60 cm",
          price: "12000",
          year: "2023",
          materials: "Tuval üzerine yağlı boya",
          description: "Modern şehir yaşamının dinamizmini yansıtan bu eser, gece ışıklarının büyüsünü tuval üzerine taşır.",
          image_url: "https://readdy.ai/api/search-image?query=Urban%20city%20lights%20oil%20painting%20at%20night&width=400&height=500&seq=home3&orientation=portrait",
          created_at: "2024-01-03"
        }
      ];
      
      setArtworks(data && data.length > 0 ? data : mockArtworks);
    } catch (error) {
      console.error('Error fetching artworks:', error);
      // Fallback to mock data on error
      const mockArtworks = [
        {
          id: 1,
          title: "Gündoğumu Yansımaları",
          artist: "Mehmet Aksoy",
          technique: "Yağlı Boya",
          size: "70x50 cm",
          price: "8500"
        },
        {
          id: 2,
          title: "Sakin Orman",
          artist: "Ayşe Demir",
          technique: "Sulu Boya",
          size: "60x40 cm",
          price: "4200"
        },
        {
          id: 3,
          title: "Şehir Işıkları",
          artist: "Can Yılmaz",
          technique: "Yağlı Boya",
          size: "80x60 cm",
          price: "12000"
        }
      ];
      setArtworks(mockArtworks);
    }
  };

  const addToCart = (id: number) => {
    setCartItems(prev => [...prev, id]);
  };

  const proceedToCheckout = () => {
    setShowCartModal(false);
    setShowCheckoutModal(true);
  };

  const handleCheckoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form verilerini topla
    const formData = new FormData();
    formData.append('name', checkoutForm.name);
    formData.append('email', checkoutForm.email);
    formData.append('phone', checkoutForm.phone);
    formData.append('address', checkoutForm.address);
    formData.append('city', checkoutForm.city);
    formData.append('postalCode', checkoutForm.postalCode);
    formData.append('paymentMethod', checkoutForm.paymentMethod);
    
    // Sipariş edilen ürünleri ekle
    const orderItems = cartItems.map(itemId => {
      const artwork = artworks.find(art => art.id === itemId);
      return artwork ? `${artwork.title} - ${artwork.artist} (${artwork.size}) - ${formatPrice(artwork.price)}` : '';
    }).filter(Boolean).join(', ');
    
    formData.append('orderItems', orderItems);
    formData.append('totalAmount', (calculateTotal() * 1.2).toLocaleString('tr-TR') + ' TL');

    try {
      const response = await fetch('https://readdy.ai/api/form/checkout-form', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // Başarılı sipariş
        setShowCheckoutModal(false);
        setCartItems([]);
        setCheckoutForm({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          postalCode: '',
          paymentMethod: 'bank-transfer'
        });
        
        // Başarı mesajı göster
        alert('Siparişiniz başarıyla alındı! Kısa süre içinde sizinle iletişime geçeceğiz.');
      } else {
        throw new Error('Sipariş gönderilemedi');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Sipariş gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, itemId) => {
      const artwork = artworks.find(art => art.id === itemId);
      if (!artwork) return total;
      const numericPrice = typeof artwork.price === 'string' ? 
        parseFloat(artwork.price.replace(/[^\d]/g, '')) : artwork.price;
      return total + numericPrice;
    }, 0);
  };

  const formatPrice = (price: string | number) => {
    if (typeof price === 'string' && price.includes('TL+KDV')) {
      return price;
    }
    const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^\d]/g, '')) : price;
    return `${numericPrice.toLocaleString('tr-TR')} TL+KDV`;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header 
        cartCount={cartItems.length} 
        onCartClick={() => setShowCartModal(true)}
        onSearchClick={() => {
          if (window.REACT_APP_NAVIGATE) {
            window.REACT_APP_NAVIGATE('/gallery');
            setTimeout(() => {
              const searchButton = document.querySelector('[data-search-trigger]') as HTMLElement;
              if (searchButton) {
                searchButton.click();
              }
            }, 100);
          }
        }}
      />
      <Hero />
      <Categories />
      <FeaturedArtworks onAddToCart={addToCart} />
      
      {/* About Section */}
      <section id="about-section" className="py-20 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">Hakkımızda</h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="text-left">
                <h3 className="text-2xl font-semibold mb-6 text-white">Güvenilir Sanat Platformu</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Nox Sanat olarak, sertifikalı ve özgün sanat eserlerini uygun maliyetlerle 
                  koleksiyonerlere ve sanat severlere güvenilir bir şekilde ulaştırmayı hedefliyoruz. 
                  2015 yılından beri faaliyet gösteren galerimiz, sanat dünyasında kalite ve 
                  güvenilirliğin simgesi haline gelmiştir.
                </p>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Her eserimiz titizlikle seçilmiş, orijinalliği doğrulanmış ve sertifikalandırılmış 
                  çalışmalardır. Sanatın herkes tarafından erişilebilir olması gerektiğine inanıyor, 
                  bu doğrultuda geniş bir fiyat yelpazesinde kaliteli eserler sunuyoruz.
                </p>
                <div className="grid grid-cols-2 gap-6 mt-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">50+</div>
                    <div className="text-gray-400">Mutlu Koleksiyoner</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white mb-2">100+</div>
                    <div className="text-gray-400">Sertifikalı Eser</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20art%20gallery%20interior%20with%20certified%20artworks%20displayed%20on%20walls%2C%20elegant%20gallery%20lighting%20showcasing%20authentic%20paintings%2C%20sophisticated%20art%20collection%20display%2C%20museum%20quality%20presentation%20with%20certificates%20and%20professional%20curation%2C%20trustworthy%20art%20gallery%20atmosphere&width=500&height=600&seq=about-gallery&orientation=portrait"
                  alt="Sanat Galerisi"
                  className="rounded-lg shadow-2xl object-cover w-full h-96"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6">İletişim</h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Sorularınız, özel sipariş talepleriniz veya sanat danışmanlığı için bizimle iletişime geçin.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-lg">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Adres</h3>
                    <p className="text-gray-400">
                      Sanat Sokağı No: 42<br />
                      Beyoğlu, İstanbul 34433<br />
                      Türkiye
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-lg">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Telefon</h3>
                    <p className="text-gray-400">+90 212 555 0123</p>
                    <p className="text-gray-400">+90 532 555 0123</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-lg">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">E-posta</h3>
                    <p className="text-gray-400">info@noxsanat.com</p>
                    <p className="text-gray-400">siparis@noxsanat.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-lg">
                    <i className="ri-time-line text-xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Çalışma Saatleri</h3>
                    <p className="text-gray-400">Pazartesi - Cuma: 09:00 - 18:00</p>
                    <p className="text-gray-400">Cumartesi: 10:00 - 16:00</p>
                    <p className="text-gray-400">Pazar: Kapalı</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-gray-900 rounded-lg p-8">
                <h3 className="text-2xl font-semibold mb-6">Bize Yazın</h3>
                <form className="space-y-6" action="https://readdy.ai/api/form/contact-form" method="POST" data-readdy-form id="contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                        placeholder="Adınız ve soyadınız"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                        placeholder="E-posta adresiniz"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                      placeholder="Telefon numaranız"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Konu *
                    </label>
                    <select
                      name="subject"
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent text-sm pr-8"
                      required
                    >
                      <option value="">Konu seçiniz</option>
                      <option value="genel-bilgi">Genel Bilgi</option>
                      <option value="ozel-siparis">Özel Sipariş</option>
                      <option value="fiyat-teklifi">Fiyat Teklifi</option>
                      <option value="sanat-danismanligi">Sanat Danışmanlığı</option>
                      <option value="sikayet">Şikayet</option>
                      <option value="diger">Diğer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Mesajınız *
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm resize-none"
                      placeholder="Mesajınızı buraya yazın... (Maksimum 500 karakter)"
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-white text-black py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Mesaj Gönder
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />

      {/* Cart Modal */}
      {showCartModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Alışveriş Sepeti</h2>
              <button 
                onClick={() => setShowCartModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-96">
              {cartItems.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-shopping-bag-line text-6xl text-gray-600 mb-4"></i>
                  <p className="text-gray-400 text-lg">Sepetiniz boş</p>
                  <p className="text-gray-500 text-sm mt-2">Galerimizdeki eserleri sepete ekleyerek başlayın</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cartItems.map((itemId, index) => {
                    const artwork = artworks.find(art => art.id === itemId);
                    if (!artwork) return null;
                    
                    return (
                      <div key={`${itemId}-${index}`} className="flex items-center gap-4 bg-gray-800 rounded-lg p-4">
                        <img 
                          src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder&width=80&height=100&seq=cartplaceholder&orientation=portrait"}
                          alt={artwork.title}
                          className="w-16 h-20 object-cover object-top rounded"
                        />
                        <div className="flex-1">
                          <h3 className="font-medium">{artwork.title}</h3>
                          <p className="text-gray-400 text-sm">{artwork.artist}</p>
                          <p className="text-gray-500 text-xs">{artwork.size}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatPrice(artwork.price)}</p>
                          <button 
                            onClick={() => setCartItems(prev => prev.filter((item, idx) => idx !== index))}
                            className="text-red-400 hover:text-red-300 transition-colors cursor-pointer text-sm"
                          >
                            Kaldır
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {cartItems.length > 0 && (
              <div className="p-6 border-t border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-medium">Toplam:</span>
                  <span className="text-2xl font-bold">
                    {cartItems.reduce((total, itemId) => {
                      const artwork = artworks.find(art => art.id === itemId);
                      if (!artwork) return total;
                      const numericPrice = typeof artwork.price === 'string' ? 
                        parseFloat(artwork.price.replace(/[^\d]/g, '')) : artwork.price;
                      return total + numericPrice;
                    }, 0).toLocaleString('tr-TR')} TL+KDV
                  </span>
                </div>
                <button 
                  onClick={() => {
                    setShowCartModal(false);
                    // Sepet verilerini localStorage'a kaydet
                    localStorage.setItem('cartItems', JSON.stringify(cartItems));
                    if (window.REACT_APP_NAVIGATE) {
                      window.REACT_APP_NAVIGATE('/checkout');
                    }
                  }}
                  className="w-full bg-white text-black py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ödemeye Geç
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Sipariş Tamamla</h2>
              <button 
                onClick={() => setShowCheckoutModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Sipariş Formu */}
                <div>
                  <h3 className="text-xl font-semibold mb-6">Sipariş Bilgileri</h3>
                  <form onSubmit={handleCheckoutSubmit} className="space-y-4" data-readdy-form id="checkout-form">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Ad Soyad *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={checkoutForm.name}
                          onChange={(e) => setCheckoutForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                          placeholder="Adınız ve soyadınız"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          E-posta *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={checkoutForm.email}
                          onChange={(e) => setCheckoutForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                          placeholder="E-posta adresiniz"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={checkoutForm.phone}
                        onChange={(e) => setCheckoutForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                        placeholder="Telefon numaranız"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Adres *
                      </label>
                      <textarea
                        name="address"
                        value={checkoutForm.address}
                        onChange={(e) => setCheckoutForm(prev => ({ ...prev, address: e.target.value }))}
                        rows={3}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm resize-none"
                        placeholder="Teslimat adresiniz (Maksimum 500 karakter)"
                        required
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Şehir *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={checkoutForm.city}
                          onChange={(e) => setCheckoutForm(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                          placeholder="Şehir"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Posta Kodu
                        </label>
                        <input
                          type="text"
                          name="postalCode"
                          value={checkoutForm.postalCode}
                          onChange={(e) => setCheckoutForm(prev => ({ ...prev, postalCode: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                          placeholder="Posta kodu"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ödeme Yöntemi *
                      </label>
                      <select
                        name="paymentMethod"
                        value={checkoutForm.paymentMethod}
                        onChange={(e) => setCheckoutForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-white focus:border-transparent text-sm pr-8"
                        required
                      >
                        <option value="bank-transfer">Havale/EFT</option>
                        <option value="credit-card">Kredi Kartı</option>
                        <option value="cash-on-delivery">Kapıda Ödeme</option>
                      </select>
                    </div>

                    <div className="pt-4">
                      <button
                        type="submit"
                        className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Siparişi Tamamla
                      </button>
                    </div>
                  </form>
                </div>

                {/* Sipariş Özeti */}
                <div>
                  <h3 className="text-xl font-semibold mb-6">Sipariş Özeti</h3>
                  <div className="bg-gray-800 rounded-lg p-6">
                    <div className="space-y-4 mb-6">
                      {cartItems.map((itemId, index) => {
                        const artwork = artworks.find(art => art.id === itemId);
                        if (!artwork) return null;
                        
                        return (
                          <div key={`${itemId}-${index}`} className="flex items-center gap-3 pb-4 border-b border-gray-700 last:border-b-0">
                            <img 
                              src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder&width=60&height=75&seq=checkoutplaceholder&orientation=portrait"}
                              alt={artwork.title}
                              className="w-12 h-15 object-cover object-top rounded"
                            />
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">{artwork.title}</h4>
                              <p className="text-gray-400 text-xs">{artwork.artist} - {artwork.size}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-sm">{formatPrice(artwork.price)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="space-y-3 pt-4 border-t border-gray-700">
                      <div className="flex justify-between text-sm">
                        <span>Ara Toplam:</span>
                        <span>{calculateTotal().toLocaleString('tr-TR')} TL</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>KDV (%20):</span>
                        <span>{(calculateTotal() * 0.2).toLocaleString('tr-TR')} TL</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Kargo:</span>
                        <span className="text-green-400">Ücretsiz</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-700">
                        <span>Toplam:</span>
                        <span>{(calculateTotal() * 1.2).toLocaleString('tr-TR')} TL</span>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
                      <div className="flex items-start gap-3">
                        <i className="ri-information-line text-blue-400 text-lg mt-0.5"></i>
                        <div className="text-sm text-blue-300">
                          <p className="font-medium mb-1">Ödeme Bilgileri</p>
                          <p className="text-xs leading-relaxed">
                            Siparişiniz onaylandıktan sonra ödeme talimatları e-posta ile gönderilecektir. 
                            Tüm eserlerimiz sigortalı kargo ile gönderilir.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
